import { PreSessionFactory } from '@/bridge/factory/PreSessionFactory';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { useNavigate } from 'react-router-dom';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import {
  AuthProvider,
  WebInSessionError,
  SessionState,
  IAuthRelativePath,
  SoloNativeRTCChannel,
} from '@amzn/work-spaces-web-client-core-lib';

const logger = CoreFactory.getLogger();
const authManager = PreSessionFactory.getAuthenticationManagerModule();
const sessionManager = CoreFactory.getSessionManager();
const rtcChannel = CoreFactory.getRTCChannel();
const device = CoreFactory.getDevice();

export const useAuthManager = () => {
  const navigate = useNavigate();
  const { showError } = useErrorHandler();

  const init = () => {
    authManager.init();
  };

  const reconnect = () => {
    const nextAuthStep = authManager.reconnect();
    navigate(nextAuthStep.complete ? '/session' : nextAuthStep.url);
  };

  const goToNextAuthStep = () => {
    try {
      const nextAuthStep = authManager.determineAuthStep();
      navigate(`${nextAuthStep.url}${window.location.search}`);
    } catch (error) {
      logger.error('Error while deciding the next auth step');
      showError(error);
    }
  };

  const fallback = () => {
    try {
      const nextAuthStep = authManager.fallback();
      navigateToAuthUrl(nextAuthStep.url);
    } catch (error) {
      logger.error('Error while perform fallback auth process');
      showError(error);
    }
  };

  const disconnect = (
    disconnectErrorType: WebInSessionError.WspErrorTypes,
    disconnectErrorCode: WebInSessionError.WspErrorCodes
  ) => {
    navigate(
      `/disconnect?${WebInSessionError.DISCONNECT_ERROR_TYPE_SEARCH_NAME}=${disconnectErrorType}&${WebInSessionError.DISCONNECT_STATUS_CODE_SEARCH_NAME}=${disconnectErrorCode}`
    );
  };

  const performAuthStep = async (step: IAuthRelativePath) => {
    try {
      const nextStep = await authManager.performAuthStep(step);
      if (nextStep.complete) {
        return navigate('/session');
      } else if (!('payload' in nextStep)) {
        if (nextStep.external) {
          navigateToAuthUrl(nextStep.url);
        } else {
          navigate(nextStep.url);
        }
      }
    } catch (error) {
      logger.error(`Error while performing an auth step - ${error}`);
      showError(error);
    }
  };

  const navigateToAuthUrl = (baseUrl?: string) => {
    try {
      // Get the stateID set during getAuthInfo context from the session store
      const clientStateID = sessionManager.get('clientStateID');
      const nextAuthMethod = sessionManager.get('nextAuthMethod');
      const externalAuthUrl = authManager.buildExternalAuthUrl(
        baseUrl,
        clientStateID
      );

      handleExternalAuthRedirectForSolo(externalAuthUrl);
      // When AuthMethod is WarpDrive and if we're running Solo linux in embedded-mode
      // or we are not in Solo Context, we should
      // load the external auth URL in browser
      if (
        !SoloNativeRTCChannel.isChannelAvailable() ||
        (!device.getIsOemHandshaked() &&
          nextAuthMethod?.AuthProvider === AuthProvider.WARP_DRIVE)
      ) {
        window.location.assign(externalAuthUrl);
      } else {
        logger.info(
          'Did not redirect to external auth page, since it is Solo non-Warp Drive case.'
        );
      }
    } catch (error) {
      logger.error(`Error while building external auth url - ${error}`);
      showError(error);
    }
  };

  const handleExternalAuthRedirectForSolo = (externalAuthUrl: URL) => {
    if (!SoloNativeRTCChannel.isChannelAvailable() || !rtcChannel) {
      return;
    }

    const nextAuthMethod = sessionManager.get('nextAuthMethod');
    const failedRedirectFallbackUrl = authManager
      .buildExternalAuthRedirectFailedErrorHandlerUrl()
      .toString();
    const authProvider: AuthProvider =
      nextAuthMethod?.AuthProvider as AuthProvider;

    // Update session state
    let sessionState = SessionState.WARP_DRIVE_REDIRECTION_START;
    if (nextAuthMethod?.AuthProvider === AuthProvider.SAML_IAM) {
      sessionState = SessionState.SAML_IAM_REDIRECTION_START;
    } else if (nextAuthMethod?.AuthProvider === AuthProvider.IDC) {
      sessionState = SessionState.IDC_IAM_REDIRECTION_START;
    }
    sessionManager.set({ sessionState });

    if (device.getIsOemHandshaked()) {
      // Publish displayWarpDriveUrl
      logger.info(
        `Sending the WarpDrive URL to the Solo Client as its running in the embedded mode: ${authProvider}`
      );
      rtcChannel.displayWarpDriveUrl(
        externalAuthUrl.toString(),
        authProvider,
        failedRedirectFallbackUrl
      );
      logger.info(
        `Auth URL dispatched to Solo via RTC. AuthProvider: ${authProvider}`
      );
    } else {
      // Create state change update and publish
      const UseBrokerProtocolV2 = sessionManager.isUseBrokerProtocolV2();
      const externalAuthRedirectConfig = {
        AuthRedirectUrl: externalAuthUrl.toString(),
        FailedRedirectFallbackUrl: failedRedirectFallbackUrl,
        UseBrokerProtocolV2,
      };

      logger.info(
        `Publishing External Auth URL redirect update to native client for provider ${nextAuthMethod?.AuthProvider}`
      );
      logger.info(
        `Identified Client Auth Capability BrokerProtocolV2: ${UseBrokerProtocolV2}`
      );

      rtcChannel.publishStateChangeUpdate(
        sessionState,
        externalAuthRedirectConfig
      );
    }
  };
  return {
    init,
    reconnect,
    fallback,
    disconnect,
    navigateToAuthUrl,
    goToNextAuthStep,
    performAuthStep,
  };
};
